/**
 * Sector operations — Construction starter templates catalogue and definitions.
 * Implements R39 B8.a (SO-D19, SO-P22, SO-P205, C§2, C§9.4).
 * All templates are starters, labelled "starter, not certified", check before use.
 */

import type { OpsTemplateField } from './registers.js'
import type { ConPourChecklistItem } from './conQuality.js'

export const CON_STARTER_SOURCE = 'timelining:construction:starter'
export const CON_STARTER_EDITION = '2026.1'
export const CON_STARTER_JURISDICTION = 'IN'

export const LEGACY_CON_DPR_TEMPLATE_ID = 'con_dpr'
export const LEGACY_CON_POUR_CARD_TEMPLATE_ID = 'con_pour_card'

/**
 * Builds a deterministic workspace-scoped template ID.
 * Follows the buildStaysTemplateId convention.
 */
export function buildConTemplateId(workspaceId: string, slug: string): string {
  const sanitized = slug.toLowerCase().replace(/[^a-z0-9_]/g, '_')
  return `tpl_con_${workspaceId}_${sanitized}`
}

export function isConDprTemplateId(templateId: string, workspaceId: string): boolean {
  return templateId === LEGACY_CON_DPR_TEMPLATE_ID || templateId === buildConTemplateId(workspaceId, 'dpr')
}

export function isConPourCardTemplateId(templateId: string, workspaceId: string): boolean {
  return templateId === LEGACY_CON_POUR_CARD_TEMPLATE_ID || templateId === buildConTemplateId(workspaceId, 'pour_card')
}

export interface ConStarterTemplateDef {
  starterKey: string
  slug: string
  name: string
  kind: 'DPR' | 'CHECK' | 'LOG'
  safetyTagged: boolean
  patientLinked: false
  lateAfterMinutes: number
  source: string
  edition: string
  jurisdiction: string
  starter: true
  fields: OpsTemplateField[]
}

// ---------------------------------------------------------------------------
// 1. DPR Starter Template Fields
// ---------------------------------------------------------------------------

export const CON_STARTER_DPR_FIELDS: OpsTemplateField[] = [
  { key: 'weather', type: 'text', label: 'NLSOpsConFieldWeather', required: false } as unknown as OpsTemplateField,
  { key: 'safetyExceptions', type: 'boolean', label: 'NLSOpsConFieldSafetyExceptions', required: false } as unknown as OpsTemplateField,
  { key: 'safetyNotes', type: 'text', label: 'NLSOpsConFieldSafetyNotes', required: false } as unknown as OpsTemplateField,
  { key: 'keyReceiptsNotes', type: 'text', label: 'NLSOpsConFieldKeyReceipts', required: false } as unknown as OpsTemplateField,
  { key: 'inspectionRefsNotes', type: 'text', label: 'NLSOpsConFieldInspectionRefs', required: false } as unknown as OpsTemplateField,
]

// ---------------------------------------------------------------------------
// 2. Pour Card & Pre-Concealment (MEP) Checklists
// ---------------------------------------------------------------------------

export const CON_STARTER_MEP_CHECKLIST: readonly ConPourChecklistItem[] = [
  {
    itemKey: 'conduit_sleeve_positions',
    label: 'NLSOpsConMepItemConduitPositions',
    result: 'OK',
  },
  {
    itemKey: 'concealed_pipe_pressure_test',
    label: 'NLSOpsConMepItemPressureTest',
    result: 'OK',
  },
  {
    itemKey: 'earthing_continuity',
    label: 'NLSOpsConMepItemEarthingContinuity',
    result: 'OK',
  },
  {
    itemKey: 'junction_boxes_fixed',
    label: 'NLSOpsConMepItemJunctionBoxes',
    result: 'OK',
  },
  {
    itemKey: 'photos_before_closing',
    label: 'NLSOpsConMepItemPhotosBeforeClosing',
    result: 'OK',
  },
]

// ---------------------------------------------------------------------------
// 3. Trade Checklists (Block-work, Plaster, Tile, Safety Walk, Material Receipt)
// ---------------------------------------------------------------------------

export const CON_STARTER_BLOCKWORK_FIELDS: OpsTemplateField[] = [
  { key: 'line_and_plumb', type: 'YES_NO', label: 'NLSOpsConBlockworkLinePlumb', required: true, exceptionOn: 'NO' },
  { key: 'mortar_joint_thickness', type: 'YES_NO', label: 'NLSOpsConBlockworkMortarJoints', required: true, exceptionOn: 'NO' },
  { key: 'lintel_bed_reinforcement', type: 'YES_NO', label: 'NLSOpsConBlockworkReinforcement', required: true, exceptionOn: 'NO' },
  { key: 'curing_status', type: 'YES_NO', label: 'NLSOpsConBlockworkCuring', required: true, exceptionOn: 'NO' },
]

export const CON_STARTER_PLASTER_FIELDS: OpsTemplateField[] = [
  { key: 'surface_hacking_cleaning', type: 'YES_NO', label: 'NLSOpsConPlasterSurfacePrep', required: true, exceptionOn: 'NO' },
  { key: 'chicken_mesh_at_joints', type: 'YES_NO', label: 'NLSOpsConPlasterMeshAtJoints', required: true, exceptionOn: 'NO' },
  { key: 'plumb_bullmarks_thickness', type: 'YES_NO', label: 'NLSOpsConPlasterPlumbThickness', required: true, exceptionOn: 'NO' },
  { key: 'curing_and_finish', type: 'YES_NO', label: 'NLSOpsConPlasterCuringFinish', required: true, exceptionOn: 'NO' },
]

export const CON_STARTER_TILE_FIELDS: OpsTemplateField[] = [
  { key: 'substrate_preparation_level', type: 'YES_NO', label: 'NLSOpsConTileSubstrateLevel', required: true, exceptionOn: 'NO' },
  { key: 'slope_towards_drains', type: 'YES_NO', label: 'NLSOpsConTileSlopeDrains', required: true, exceptionOn: 'NO' },
  { key: 'spacing_alignment', type: 'YES_NO', label: 'NLSOpsConTileSpacingAlignment', required: true, exceptionOn: 'NO' },
  { key: 'hollow_sound_debonding', type: 'YES_NO', label: 'NLSOpsConTileHollowSoundCheck', required: true, exceptionOn: 'NO' },
  { key: 'grouting_cleaning', type: 'YES_NO', label: 'NLSOpsConTileGroutingCleaning', required: true, exceptionOn: 'NO' },
]

export const CON_STARTER_SAFETY_WALK_FIELDS: OpsTemplateField[] = [
  { key: 'ppe_compliance', type: 'YES_NO', label: 'NLSOpsConSafetyPpeCompliance', required: true, exceptionOn: 'NO' },
  { key: 'edge_protection_floor_openings', type: 'YES_NO', label: 'NLSOpsConSafetyEdgeProtection', required: true, exceptionOn: 'NO' },
  { key: 'scaffold_inspection_tags', type: 'YES_NO', label: 'NLSOpsConSafetyScaffoldTags', required: true, exceptionOn: 'NO' },
  { key: 'housekeeping_accessways', type: 'YES_NO', label: 'NLSOpsConSafetyHousekeeping', required: true, exceptionOn: 'NO' },
  { key: 'electrical_distribution_boards', type: 'YES_NO', label: 'NLSOpsConSafetyElectricalBoards', required: true, exceptionOn: 'NO' },
  { key: 'first_aid_box_stocked', type: 'YES_NO', label: 'NLSOpsConSafetyFirstAidBox', required: true, exceptionOn: 'NO' },
]

export const CON_STARTER_MATERIAL_RECEIPT_FIELDS: OpsTemplateField[] = [
  { key: 'delivery_note_matched', type: 'YES_NO', label: 'NLSOpsConMatReceiptDeliveryNoteMatched', required: true, exceptionOn: 'NO' },
  { key: 'quantity_counted', type: 'YES_NO', label: 'NLSOpsConMatReceiptQuantityCounted', required: true, exceptionOn: 'NO' },
  { key: 'visible_damage_check', type: 'YES_NO', label: 'NLSOpsConMatReceiptVisibleDamage', required: true, exceptionOn: 'NO' },
  { key: 'test_certificate_attached', type: 'YES_NO', label: 'NLSOpsConMatReceiptTestCertAttached', required: true, exceptionOn: 'NO' },
  { key: 'sample_taken', type: 'YES_NO', label: 'NLSOpsConMatReceiptSampleTaken', required: true, exceptionOn: 'NO' },
]

// ---------------------------------------------------------------------------
// 4. Starter Template Definitions Map
// ---------------------------------------------------------------------------

export const CON_STARTER_TEMPLATES: Record<string, ConStarterTemplateDef> = {
  dpr: {
    starterKey: 'CON_DPR',
    slug: 'dpr',
    name: 'NLSOpsConTplDprName',
    kind: 'DPR',
    safetyTagged: false,
    patientLinked: false,
    lateAfterMinutes: 1440,
    source: CON_STARTER_SOURCE,
    edition: CON_STARTER_EDITION,
    jurisdiction: CON_STARTER_JURISDICTION,
    starter: true,
    fields: CON_STARTER_DPR_FIELDS,
  },
  pour_card: {
    starterKey: 'CON_POUR_CARD',
    slug: 'pour_card',
    name: 'NLSOpsConTplPourCardName',
    kind: 'CHECK',
    safetyTagged: true,
    patientLinked: false,
    lateAfterMinutes: 1440,
    source: CON_STARTER_SOURCE,
    edition: CON_STARTER_EDITION,
    jurisdiction: CON_STARTER_JURISDICTION,
    starter: true,
    fields: [],
  },
  pre_concealment: {
    starterKey: 'CON_PRE_CONCEALMENT',
    slug: 'pre_concealment',
    name: 'NLSOpsConTplPreConcealmentName',
    kind: 'CHECK',
    safetyTagged: true,
    patientLinked: false,
    lateAfterMinutes: 1440,
    source: CON_STARTER_SOURCE,
    edition: CON_STARTER_EDITION,
    jurisdiction: CON_STARTER_JURISDICTION,
    starter: true,
    fields: [],
  },
  blockwork_check: {
    starterKey: 'CON_CHECK_BLOCKWORK',
    slug: 'blockwork_check',
    name: 'NLSOpsConTplBlockworkCheckName',
    kind: 'CHECK',
    safetyTagged: false,
    patientLinked: false,
    lateAfterMinutes: 1440,
    source: CON_STARTER_SOURCE,
    edition: CON_STARTER_EDITION,
    jurisdiction: CON_STARTER_JURISDICTION,
    starter: true,
    fields: CON_STARTER_BLOCKWORK_FIELDS,
  },
  plaster_check: {
    starterKey: 'CON_CHECK_PLASTER',
    slug: 'plaster_check',
    name: 'NLSOpsConTplPlasterCheckName',
    kind: 'CHECK',
    safetyTagged: false,
    patientLinked: false,
    lateAfterMinutes: 1440,
    source: CON_STARTER_SOURCE,
    edition: CON_STARTER_EDITION,
    jurisdiction: CON_STARTER_JURISDICTION,
    starter: true,
    fields: CON_STARTER_PLASTER_FIELDS,
  },
  tile_check: {
    starterKey: 'CON_CHECK_TILE',
    slug: 'tile_check',
    name: 'NLSOpsConTplTileCheckName',
    kind: 'CHECK',
    safetyTagged: false,
    patientLinked: false,
    lateAfterMinutes: 1440,
    source: CON_STARTER_SOURCE,
    edition: CON_STARTER_EDITION,
    jurisdiction: CON_STARTER_JURISDICTION,
    starter: true,
    fields: CON_STARTER_TILE_FIELDS,
  },
  safety_walk_basic: {
    starterKey: 'CON_CHECK_SAFETY_WALK',
    slug: 'safety_walk_basic',
    name: 'NLSOpsConTplSafetyWalkName',
    kind: 'CHECK',
    safetyTagged: true,
    patientLinked: false,
    lateAfterMinutes: 1440,
    source: CON_STARTER_SOURCE,
    edition: CON_STARTER_EDITION,
    jurisdiction: CON_STARTER_JURISDICTION,
    starter: true,
    fields: CON_STARTER_SAFETY_WALK_FIELDS,
  },
  material_receipt_inspection: {
    starterKey: 'CON_CHECK_MATERIAL_RECEIPT',
    slug: 'material_receipt_inspection',
    name: 'NLSOpsConTplMaterialReceiptName',
    kind: 'CHECK',
    safetyTagged: false,
    patientLinked: false,
    lateAfterMinutes: 1440,
    source: CON_STARTER_SOURCE,
    edition: CON_STARTER_EDITION,
    jurisdiction: CON_STARTER_JURISDICTION,
    starter: true,
    fields: CON_STARTER_MATERIAL_RECEIPT_FIELDS,
  },
}

// ---------------------------------------------------------------------------
// 5. Snag Categories by Room Type
// ---------------------------------------------------------------------------

export const CON_SNAG_ROOM_TYPES = [
  'bedroom',
  'kitchen',
  'bathroom',
  'living',
  'balcony',
  'common_area',
  'external',
] as const
export type ConSnagRoomType = (typeof CON_SNAG_ROOM_TYPES)[number]

export const CON_SNAG_CATEGORIES_BY_ROOM_TYPE: Record<ConSnagRoomType, readonly string[]> = {
  bedroom: ['civil', 'plaster', 'paint', 'tile', 'joinery', 'electrical'],
  kitchen: ['civil', 'plaster', 'paint', 'tile', 'joinery', 'plumbing', 'electrical', 'waterproofing'],
  bathroom: ['civil', 'plaster', 'paint', 'tile', 'plumbing', 'electrical', 'waterproofing', 'glazing'],
  living: ['civil', 'plaster', 'paint', 'tile', 'joinery', 'electrical', 'glazing'],
  balcony: ['civil', 'plaster', 'paint', 'tile', 'waterproofing', 'glazing', 'joinery'],
  common_area: ['civil', 'plaster', 'paint', 'tile', 'electrical', 'joinery'],
  external: ['civil', 'plaster', 'paint', 'waterproofing', 'glazing', 'plumbing'],
}

export const ALL_CON_SNAG_CATEGORIES: readonly string[] = [
  'civil',
  'plaster',
  'paint',
  'tile',
  'joinery',
  'plumbing',
  'electrical',
  'glazing',
  'waterproofing',
  'other',
] as const

export function getSnagCategoriesForRoomType(roomType?: string | null): readonly string[] {
  if (!roomType) return ALL_CON_SNAG_CATEGORIES
  const normalized = roomType.toLowerCase().trim().replace(/[^a-z0-9_]/g, '_') as ConSnagRoomType
  return CON_SNAG_CATEGORIES_BY_ROOM_TYPE[normalized] || ALL_CON_SNAG_CATEGORIES
}

// ---------------------------------------------------------------------------
// 6. Starter Templates Status Types
// ---------------------------------------------------------------------------

export type ConStarterTemplateSlug = keyof typeof CON_STARTER_TEMPLATES
export const CON_STARTER_SLUGS = Object.keys(CON_STARTER_TEMPLATES) as ConStarterTemplateSlug[]
export type ConStarterTemplateDefinition = ConStarterTemplateDef

export interface ConStarterTemplateStatus {
  starterKey: string
  slug: string
  name: string
  templateId: string
  currentVersion: number
  edition: string
  edited: boolean
  exists: boolean
}

export interface GetConStarterStatusInput {
  workspaceId: string
  projectId?: string
}

export interface GetConStarterStatusResult {
  templates: ConStarterTemplateStatus[]
}

